import { Icon, IconName } from '@clarion/ClarionIcon';
import { useCallback } from 'react';
import {
  Text,
  UNSTABLE_Toast as Toast,
  UNSTABLE_ToastContent as ToastContent,
} from 'react-aria-components';
import { QueuedToast } from 'react-stately';
import { tv } from 'tailwind-variants';
import { cx } from '../util';
import { ClarionButton } from './ClarionButton';
import { ClarionButtonLink } from './ClarionButtonLink';
import type { ToastProps } from './ClarionToaster';

export type ClarionToastComponentProps = {
  toast: QueuedToast<ToastProps>;
  onClose: (key: string) => void;
  className?: string;
};

const toastStyles = tv({
  base: [
    'flex p-2 rounded-lg shadow-md border max-w-md my-2 min-h-12 items-center',
    'pointer-events-auto',
    'animate-in slide-in-from-right-full fade-in duration-300',
    'data-[exiting]:animate-out data-[exiting]:slide-out-to-right-full data-[exiting]:fade-out exiting:duration-200',
    'forced-colors:border-[ButtonBorder] forced-colors:border-3',
  ],
  variants: {
    intent: {
      none: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100',
      primary: 'bg-purple-600 border-transparent dark:border-white/20 text-white',
      success: 'bg-green-600 border-transparent dark:border-white/20 text-white',
      warning: 'bg-orange-600 border-transparent dark:border-white/20 text-white',
      danger: 'bg-red-600 border-transparent dark:border-white/20 text-white',
    },
  },
});

const iconStyles = tv({
  base: 'shrink-0 flex items-center',
  variants: {
    intent: {
      none: 'text-gray-500 dark:text-gray-400',
      primary: 'text-white',
      success: 'text-white',
      warning: 'text-white',
      danger: 'text-white',
    },
  },
});

function getDefaultIcon(intent: string): IconName {
  switch (intent) {
    case 'success':
      return 'tick';
    case 'warning':
      return 'warning-sign';
    case 'danger':
      return 'alert-octagon';
    case 'primary':
      return 'info-sign';
    default:
      return 'blank';
  }
}

export function ClarionToastComponent({ toast, onClose, className }: ClarionToastComponentProps) {
  const { content } = toast;
  const intent = content.intent || 'none';
  const isCloseButtonShown = content.isCloseButtonShown ?? true;

  const handleDismiss = useCallback(() => {
    onClose(toast.key);
  }, [onClose, toast.key]);

  const handleActionClick = (e: any) => {
    content.action?.onClick?.(e);
    handleDismiss();
  };

  const displayIcon = content.icon || (intent !== 'none' ? getDefaultIcon(intent) : undefined);

  return (
    <Toast toast={toast} className={toastStyles({ intent, className })}>
      <ToastContent className={cx('pl-2 flex gap-3 items-center w-full')}>
        {displayIcon && <Icon icon={displayIcon} className={iconStyles({ intent })} />}
        <Text slot="title" className="grow">
          {content.message}
        </Text>

        {content.action && (
          <div className="shrink-0">
            {content.action.href ? (
              <ClarionButtonLink
                variant="minimal"
                size="small"
                {...content.action}
                children={content.action.text}
                onClick={handleActionClick}
                className={cx(
                  intent !== 'none' &&
                    'text-white! hover:bg-white/20! pressed:bg-white/30! [&_.clarion-icon]:text-white!',
                )}
              />
            ) : (
              <ClarionButton
                variant="minimal"
                size="small"
                {...content.action}
                children={content.action.text}
                onClick={handleActionClick}
                className={cx(
                  intent !== 'none' &&
                    'text-white! hover:bg-white/20! pressed:bg-white/30! [&_.clarion-icon]:text-white!',
                )}
              />
            )}
          </div>
        )}

        {isCloseButtonShown && (
          <ClarionButton
            icon="cross"
            variant="minimal"
            onPress={handleDismiss}
            slot="close"
            tooltip="Close"
            className={cx(
              intent !== 'none' &&
                'text-white! hover:bg-white/20! pressed:bg-white/30! [&_.clarion-icon]:text-white!',
            )}
          />
        )}
      </ToastContent>
    </Toast>
  );
}
